<script>
  import { onMount, onDestroy } from "svelte";
  import tmwfLogo from "../assets/img/tmwf_mini_icon.png";
  import userIcon from "../assets/img/user-check.png";
  import wifiIconGreen from "../assets/img/wifi_green.png";
  import wifiIconRed from "../assets/img/wifi_red.png";
  import buyDataIcon from "../assets/img/buy_data.png";
  import loadDataIcon from "../assets/img/load_voucher.png";
  import opentimeBanner from "../assets/img/opentime_banner.png";
  import {
    getHotspotData,
    getActiveAllocation,
    getAccount,
    getCellphoneByMAC,
  } from "./utils.svelte.js";
  import { requestJSON, readPendingCheckout, savePendingCheckout, pollCheckout, clearPendingCheckout } from "./payment.js";

  const baseURL = import.meta.env.VITE_BASE_URL;
  const portalURL = import.meta.env.VITE_BASE_PORTAL_URL;

  // Props
  export let store;

  let accountPin = null;

  let loginForm;

  let voucherExpires = null;

  let days = 0;
  let hours = 0;
  let minutes = 0;

  let openTimeCurrentlyAvailable = false;
  let openTimeDisplayBanner = false;
  let openTimeGranted = false;
  let openTimeAlreadyRedeemed = false;

  let activeAllocation = false;

  let intervalId = null;
  let openTimePollId = null;
  let paymentMessage = null;
  let paymentPending = false;
  let checkingPayment = false;
  let paymentContext = null;
  const lifecycleController = new AbortController();

  async function checkActiveAllocation(cellphone) {
    const activeAllocationCall = await getActiveAllocation(cellphone);

    if (activeAllocationCall) {
      activeAllocation = true;
    } else {
      activeAllocation = false;
    }
  }

  async function submitLoginForm() {
    await getHotspotData();

    await checkActiveAllocation(store.cellphone);
    if (lifecycleController.signal.aborted || paymentPending) return;
    if (store.isAuthenticatedOnHotspot === false && activeAllocation != false) {
      if (!accountPin) {
        store.setPinLoginNotice("Enter your PIN to connect this device to the hotspot.");
        store.setState("PIN_LOGIN");
        return;
      }
      if (loginForm) {
        loginForm.submit();
      }
    }
  }

  function goToVoucherFlow(nextState) {
    if (paymentPending) return;
    store.setState(nextState);
  }

  async function getDevice() {
    if (!store.macAddress) {
      return null;
    }
    try {
      return await requestJSON(`${baseURL}device/${store.macAddress}/`, {
        method: "GET",
        signal: lifecycleController.signal,
        timeoutMs: 8000,
        headers: { "Content-Type": "application/json" },
      });
    } catch (error) {
      console.error("Error", error);
      return null;
    }
  }

  async function checkOpenTimeAvailability() {
    try {
      let response = await fetch(`${baseURL}opentime/availability/`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      openTimeCurrentlyAvailable = response.status === 200;
    } catch (error) {
      openTimeCurrentlyAvailable = false;
    }
  }

  function determineOpenTimeWorthiness() {
    openTimeDisplayBanner = Boolean(
      store.isConnectedToHotspot &&
        !voucherExpires &&
        openTimeCurrentlyAvailable &&
        !openTimeGranted &&
        !openTimeAlreadyRedeemed,
    );
  }

  async function refreshOpenTimeBanner() {
    await checkOpenTimeAvailability();
    determineOpenTimeWorthiness();
  }

  async function clickOpenTimeBanner() {
    if (!store.macAddress) {
      openTimeDisplayBanner = false;
      return;
    }
    let url = `${baseURL}opentime/allocate/?user_mac=${store.macAddress}`;
    let response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (response.status === 200) {
      openTimeGranted = true;
      openTimeAlreadyRedeemed = false;
      openTimeDisplayBanner = false;
      await dataTimer();
    } else if (response.status === 403) {
      openTimeAlreadyRedeemed = true;
      openTimeGranted = false;
      openTimeDisplayBanner = false;
    } else {
      openTimeCurrentlyAvailable = false;
      openTimeDisplayBanner = false;
    }
  }

  async function dataTimer() {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    const deviceInfo = await getDevice();
    if (!deviceInfo?.allocation?.date_expires) {
      voucherExpires = null;
      days = 0;
      hours = 0;
      minutes = 0;
      return;
    }

    voucherExpires = new Date(deviceInfo.allocation.date_expires);
    const now = new Date();

    if (voucherExpires > now) {
      const timeDiff = voucherExpires.getTime() - now.getTime();

      days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      let seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

      intervalId = setInterval(() => {
        if (seconds > 0) {
          seconds--;
        } else if (minutes > 0) {
          minutes--;
          seconds = 59;
        } else if (hours > 0) {
          hours--;
          minutes = 59;
          seconds = 59;
        } else if (days > 0) {
          days--;
          hours = 23;
          minutes = 59;
          seconds = 59;
        } else {
          clearInterval(intervalId);
          intervalId = null;
          voucherExpires = null;
        }
      }, 1000);
    } else {
      voucherExpires = null;
      days = 0;
      hours = 0;
      minutes = 0;
    }
  }

  async function getUserPin(cellphone) {
    if (store.hotspotPin) {
      accountPin = store.hotspotPin;
      return;
    }
    try {
      const account = await getAccount(cellphone);
      accountPin = account?.pin ?? null;
    } catch (error) {
      console.log(error);
      accountPin = null;
    }
  }

  // Update widget with connection status
  $: connectedWidgetGreen =
    store.isAuthenticatedOnHotspot === true &&
    store.isConnectedToHotspot === true &&
    activeAllocation === true;

  async function initialiseAccount() {
    // Update cellphone number if null
    if (store.cellphone === null) {
      await getHotspotData();

      if (store.macAddress) {
        let cellphone = await getCellphoneByMAC(store.macAddress);
        if (cellphone) {
          store.setCellphone(
            typeof cellphone === "string"
              ? cellphone
              : cellphone.cellphone || cellphone.phone_number || null,
          );
        }
      }
    }

    await getUserPin(store.cellphone);
    if (lifecycleController.signal.aborted) return;

    await checkActiveAllocation(store.cellphone);

    await submitLoginForm();
    if (lifecycleController.signal.aborted) return;

    await dataTimer();
    if (lifecycleController.signal.aborted) return;

    await refreshOpenTimeBanner();
    if (lifecycleController.signal.aborted) return;
    if (openTimePollId) clearInterval(openTimePollId);
    openTimePollId = setInterval(refreshOpenTimeBanner, 60000);
  }

  async function recoverPayment() {
    if (!paymentContext || checkingPayment) return;
    checkingPayment = true;
    paymentPending = true;
    paymentMessage = "Checking your payment...";
    try {
      const result = await pollCheckout({
        portalURL,
        context: paymentContext,
        signal: lifecycleController.signal,
        onUpdate: data => {
          if (data.cellphone) store.setCellphone(data.cellphone);
          paymentMessage = data.processed
            ? "Payment received. Your hotspot bundle is ready."
            : data.processing
              ? "Your payment is still being confirmed. Please do not pay again."
              : `Payment was not completed. ${data.description || "You can try again."}`;
        },
      });
      if (lifecycleController.signal.aborted) return;
      paymentPending = result === "pending";
      if (!paymentPending) {
        clearPendingCheckout();
        paymentContext = null;
      }
      await initialiseAccount();
    } catch (error) {
      if (!lifecycleController.signal.aborted) {
        if (error.status === 403 || error.status === 404) {
          clearPendingCheckout();
          paymentContext = null;
          paymentPending = false;
          paymentMessage = "We could not recover that checkout. Check whether you were charged before paying again.";
          await initialiseAccount();
        } else {
          paymentMessage = "We could not confirm your payment yet. Check again before paying again.";
        }
      }
    } finally {
      if (!lifecycleController.signal.aborted) checkingPayment = false;
    }
  }

  onMount(async () => {
    paymentContext = readPendingCheckout(window.location.href);
    // Keep the private token out of subsequent browser referrers/history URLs.
    if (paymentContext && savePendingCheckout(paymentContext)) {
      const url = new URL(window.location.href);
      url.searchParams.delete("checkout_id");
      url.searchParams.delete("checkout_token");
      window.history.replaceState(null, "", url);
    }
    if (paymentContext) await recoverPayment();
    else await initialiseAccount();
  });

  onDestroy(() => {
    lifecycleController.abort();
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (openTimePollId) {
      clearInterval(openTimePollId);
      openTimePollId = null;
    }
  });
</script>

<form
  bind:this={loginForm}
  name="redirect"
  action="//hotspot.toomuchwifi.co.za/login"
  method="post"
  class="hidden"
>
  <input
    type="hidden"
    name="username"
    value="{store.cellphone}@connect.toomuchwifi.co.za"
  />
  <input type="hidden" name="password" value={accountPin} />
</form>

<div class="container flex flex-col h-screen">
  <div
    class="flex-col justify-center mx-auto items-center text-center p-3 my-10 border border-gray-300 rounded-md w-11/12 max-w-[30em]"
  >
    <div class="bg-[#009946] grid size-12 rounded-full mx-auto">
      <img src={userIcon} alt="User Icon" class="m-auto w-1/2" />
    </div>
    <span class="font-bold text-lg tracking-wider">{store.cellphone}</span>
    {#if store.node}
      <p class="mt-1 text-xs font-medium tracking-wider text-gray-400">
        {store.node}
      </p>
    {/if}
    {#if store.macAddress}
      <p class="mt-0.5 text-[11px] tracking-wider text-gray-400">
        {store.macAddress}
      </p>
    {/if}
  </div>

  {#if paymentMessage}
    <div class="mx-auto mb-4 w-5/6 text-center text-sm" role="status">
      <p>{paymentMessage}</p>
      {#if paymentContext}
        <button class="mt-2 underline text-[#408bcc]" disabled={checkingPayment} on:click={recoverPayment}>
          {checkingPayment ? "Checking payment..." : "Check payment status"}
        </button>
      {/if}
    </div>
  {/if}

  {#if openTimeDisplayBanner}
    <div class="mb-7 mx-auto w-5/6 max-w-[30em]">
      <button class="hover:cursor-pointer" on:click={clickOpenTimeBanner}>
        <img
          src={opentimeBanner}
          alt="OpenTime Banner"
          class="mx-auto w-full"
        />
      </button>
    </div>
  {:else if openTimeGranted}
    <p class="mb-4 text-center text-sm font-medium text-[#009946]">
      OpenTime granted — enjoy your free hour
    </p>
  {:else if openTimeAlreadyRedeemed}
    <p class="mb-4 text-center text-sm font-medium text-gray-500">
      OpenTime already redeemed for this period
    </p>
  {/if}

  <div class="flex justify-center">
    <h2 class="font-bold mb-4 text-xl">Hotspot Time Remaining</h2>
  </div>

  <div
    class="bg-[#313131] flex mx-auto rounded-xl shadow-xl my-4 py-[2em] justify-center w-5/6"
  >
    <div class="columns-1">
      <img src={tmwfLogo} alt="TooMuchWifi Logo" class="mb-2 mx-auto w-1/2" />

      <div class="flex justify-around">
        <div class="flex flex-col items-center">
          <span id="days" class="font-bold text-5xl text-white">
            {String(days).padStart(2, "0")}
          </span>
          <span class="text-sm text-white">Days</span>
        </div>
        <div class="text-4xl text-white">:</div>
        <div class="flex flex-col items-center">
          <span id="hours" class="font-bold text-5xl text-white">
            {String(hours).padStart(2, "0")}
          </span>
          <span class="text-sm text-white">Hours</span>
        </div>
        <div class="text-4xl text-white">:</div>
        <div class="flex flex-col items-center">
          <span id="minutes" class="font-bold text-5xl text-white">
            {String(minutes).padStart(2, "0")}
          </span>
          <span class="text-sm text-white">Minutes</span>
        </div>
      </div>
    </div>
  </div>

  {#if connectedWidgetGreen}
    <div
      class="bg-[#009946] flex flex-row my-8 mx-auto p-1.5 rounded-full text-white w-56"
    >
      <div class="bg-white grid h-9 w-9 mr-2 rounded-full">
        <img src={wifiIconGreen} alt="WiFi symbol green" class="h-7 m-auto" />
      </div>
      <h1 class="font-semibold my-auto text-sm">Connected to hotspot</h1>
    </div>
  {:else}
    <button
      on:click={submitLoginForm}
      disabled={paymentPending}
      class="bg-[#dd281d] flex flex-row my-8 mx-auto p-1.5 rounded-full text-white w-56"
    >
      <div class="bg-white grid h-9 w-9 mr-2 rounded-full">
        <img src={wifiIconRed} alt="WiFi symbol red" class="h-6 m-auto" />
      </div>
      <h1 class="font-semibold ml-6 my-auto text-sm">Disconnected</h1>
    </button>
  {/if}

  <div class="flex-row flex justify-center">
    <button
      class="bg-[#408bcc] mr-2 py-3 px-2 rounded-lg shadow-md w-28"
      on:click={() => goToVoucherFlow("BUNDLE_PURCHASE")}
      disabled={paymentPending}
    >
      <img src={buyDataIcon} alt="Buy data" class="mx-auto h-6 m-1" />
      <p class="font-bold text-white text-xs whitespace-nowrap">Buy Voucher</p>
    </button>
    <button
      class="bg-[#408bcc] ml-2 py-3 px-2 rounded-md shadow-md w-28"
      on:click={() => goToVoucherFlow("VOUCHER_REDEMPTION")}
      disabled={paymentPending}
    >
      <img src={loadDataIcon} alt="Load data" class="mx-auto h-6 m-1" />
      <p class="font-bold text-white text-xs whitespace-nowrap">Load Voucher</p>
    </button>
  </div>

  <div class="my-2"></div>

  <div class="mb-2 mt-auto text-center text-sm text-gray-500 w-full">
    <button
      class="underline text-[#408bcc]"
      on:click={() => store.setState("HELP")}>Need help?</button
    >
    <p>&copy; 2024 TooMuchWifi. All rights reserved.</p>
  </div>
</div>
